import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import TerritoryMapTab from "./TerritoryMapTab";
import DispatchSettingsTab from "./DispatchSettingsTab";
import {
  Users, MapPin, ClipboardList, Clock, CheckCircle, AlertTriangle,
  Search, Eye, RefreshCw, UserCheck, X, UserPlus, Trash2,
  ToggleLeft, ToggleRight, Zap, GitBranch, Settings, XCircle, BookOpen, ClipboardCheck
} from "lucide-react";

const DISPATCH_STATUS_CONFIG = {
  auto_dispatched:    { label: "Auto-dispatched",   color: "bg-green-50 text-green-700",  dot: "bg-green-500",  icon: Zap },
  needs_decision:     { label: "Decision needed",   color: "bg-amber-50 text-amber-700",  dot: "bg-amber-500",  icon: AlertTriangle },
  completed_no_visit: { label: "Closed — No Visit", color: "bg-blue-50 text-blue-700",    dot: "bg-blue-500",   icon: CheckCircle },
  manually_assigned:  { label: "Assigned",          color: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500", icon: UserCheck },
};

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-600", dot: "bg-slate-400" },
  in_progress: { label: "In Progress", color: "bg-blue-50 text-blue-700", dot: "bg-blue-500" },
  completed: { label: "Needs Review", color: "bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  reviewed: { label: "Reviewed", color: "bg-purple-50 text-purple-700", dot: "bg-purple-500" },
  finalized: { label: "Finalized", color: "bg-green-50 text-green-700", dot: "bg-green-500" },
};

const TYPE_LABELS = {
  commercial_property: "Commercial Property",
  workers_comp: "Workers' Comp",
  habitational: "Habitational",
  general_liability: "General Liability",
  commercial_auto: "Commercial Auto",
};

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY"
];

function DispatchStatusBadge({ dispatchStatus }) {
  if (!dispatchStatus) return null;
  const cfg = DISPATCH_STATUS_CONFIG[dispatchStatus];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function DispatchDecisionModal({ inspection, team, onAction, onClose }) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [assignTarget, setAssignTarget] = useState("");
  const [saving, setSaving] = useState(false);

  const canSubmit = selectedOption === "complete" || (selectedOption === "assign" && assignTarget);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (selectedOption === "complete") {
        await onAction(inspection.id, "complete_no_visit");
      } else {
        await onAction(inspection.id, "assign", assignTarget);
      }
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full animate-slide-up">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-bold text-slate-800">Review Decision Required</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-1">{inspection?.insured_name}</p>
        <div className="flex items-center gap-2 mb-5">
          <span className="text-xs text-slate-400">{inspection?.property_address}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
            (inspection?.risk_score || 0) > 65 ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
          }`}>
            Risk Score: {inspection?.risk_score} — {inspection?.risk_level}
          </span>
        </div>

        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Select an action</p>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <button
            data-testid="option-complete-no-visit"
            onClick={() => setSelectedOption("complete")}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              selectedOption === "complete" ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <CheckCircle className={`w-5 h-5 mb-2 ${selectedOption === "complete" ? "text-blue-600" : "text-slate-400"}`} />
            <p className="font-semibold text-sm text-slate-800">Complete — No Site Visit</p>
            <p className="text-xs text-slate-500 mt-1">AI findings are sufficient. Close without a field visit.</p>
          </button>
          <button
            data-testid="option-assign-rep"
            onClick={() => setSelectedOption("assign")}
            className={`p-4 rounded-xl border-2 text-left transition-all ${
              selectedOption === "assign" ? "border-navy bg-navy/5" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <UserCheck className={`w-5 h-5 mb-2 ${selectedOption === "assign" ? "text-navy" : "text-slate-400"}`} />
            <p className="font-semibold text-sm text-slate-800">Assign to Field Rep</p>
            <p className="text-xs text-slate-500 mt-1">Send a rep for an on-site investigation.</p>
          </button>
        </div>

        {selectedOption === "assign" && (
          <div className="mb-5">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Select Rep</label>
            <select value={assignTarget} onChange={e => setAssignTarget(e.target.value)}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy/30"
              data-testid="decision-assign-select">
              <option value="">Choose a team member...</option>
              {team.map(u => (
                <option key={u.id} value={u.id}>
                  {`${u.name} — ${u.role === "lc_manager" ? "Manager" : "Field Staff"}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || saving}
            data-testid="confirm-decision-btn"
            className="flex-1 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 disabled:opacity-40 transition-colors"
          >
            {saving ? "Saving..." : "Confirm Decision"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}></span>
      {cfg.label}
    </span>
  );
}

function RiskBadge({ score, level, threshold, mode }) {
  if (score === null || score === undefined) {
    return <span className="text-xs text-slate-400 italic">—</span>;
  }
  const isAutoDispatch = mode !== "manual" && score <= threshold;
  const bgColor = score <= 40 ? "bg-green-50 text-green-700"
    : score <= 65 ? "bg-amber-50 text-amber-700"
    : "bg-red-50 text-red-700";
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${bgColor}`}>
        {score}
        <span className="font-normal text-xs opacity-75">{level ? `· ${level}` : ""}</span>
      </span>
      {mode !== "manual" && (
        <span className={`text-xs ${isAutoDispatch ? "text-green-600" : "text-amber-600"}`}>
          {isAutoDispatch ? "Auto-dispatch" : "Manual decision"}
        </span>
      )}
    </div>
  );
}

function AssignModal({ inspection, team, onAssign, onClose }) {
  const [selectedUser, setSelectedUser] = useState(inspection?.assigned_to || "");
  const [saving, setSaving] = useState(false);

  const handleAssign = async () => {
    setSaving(true);
    try {
      await onAssign(inspection.id, selectedUser);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full animate-slide-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-slate-800">Assign Inspection</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-1">{inspection?.insured_name}</p>
        <p className="text-xs text-slate-400 mb-5">{TYPE_LABELS[inspection?.inspection_type]} — {inspection?.property_address}</p>
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Assign To</label>
        <select
          value={selectedUser}
          onChange={e => setSelectedUser(e.target.value)}
          className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy/30"
          data-testid="assign-user-select"
        >
          <option value="">Unassigned</option>
          {team.map(u => (
            <option key={u.id} value={u.id}>
              {u.name} — {u.role === "lc_manager" ? "Manager" : "Staff"}
            </option>
          ))}
        </select>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button
            onClick={handleAssign}
            disabled={saving}
            className="flex-1 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 disabled:opacity-60"
            data-testid="confirm-assign-btn"
          >
            {saving ? "Saving..." : "Confirm Assignment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TerritoryEditor({ staffUser, onSave, onClose }) {
  const [territories, setTerritories] = useState(staffUser.territories || []);
  const [newState, setNewState] = useState("");
  const [newCounties, setNewCounties] = useState("");
  const [newZips, setNewZips] = useState("");
  const [autoAssign, setAutoAssign] = useState(staffUser.auto_assign_enabled !== false);
  const [saving, setSaving] = useState(false);

  const addTerritory = () => {
    if (!newState || territories.find(t => t.state === newState)) return;
    setTerritories(prev => [...prev, {
      state: newState,
      counties: newCounties.split(",").map(c => c.trim()).filter(Boolean),
      zips: newZips.split(",").map(z => z.trim()).filter(Boolean),
    }]);
    setNewState(""); setNewCounties(""); setNewZips("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(staffUser.id, { territories, auto_assign_enabled: autoAssign });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-heading font-bold text-slate-800">Edit Territories</h3>
            <p className="text-sm text-slate-500 mt-0.5">{staffUser.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {staffUser.role === "lc_manager" && (
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mb-5">
            <div>
              <p className="text-sm font-medium text-slate-700">Auto-assign Unassigned Inspections</p>
              <p className="text-xs text-slate-500 mt-0.5">Automatically route unassigned work to this manager</p>
            </div>
            <button onClick={() => setAutoAssign(!autoAssign)} data-testid="toggle-auto-assign">
              {autoAssign
                ? <ToggleRight className="w-9 h-9 text-navy" />
                : <ToggleLeft className="w-9 h-9 text-slate-400" />}
            </button>
          </div>
        )}

        <div className="space-y-2 mb-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Assigned Territories</p>
          {territories.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-3">No territories assigned</p>
          ) : territories.map(t => (
            <div key={t.state} className="flex items-start justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <div>
                <span className="font-semibold text-navy text-sm">{t.state}</span>
                {t.counties.length > 0 && <p className="text-xs text-slate-500 mt-0.5">Counties: {t.counties.join(", ")}</p>}
                {t.zips.length > 0 && <p className="text-xs text-slate-500">Zips: {t.zips.join(", ")}</p>}
              </div>
              <button onClick={() => setTerritories(prev => prev.filter(x => x.state !== t.state))} className="text-red-400 hover:text-red-600 ml-3 mt-0.5">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-slate-100 pt-5 space-y-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Add Territory</p>
          <div className="grid grid-cols-3 gap-2">
            <select value={newState} onChange={e => setNewState(e.target.value)}
              className="h-9 px-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="state-select">
              <option value="">State</option>
              {US_STATES.filter(s => !territories.find(t => t.state === s)).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input value={newCounties} onChange={e => setNewCounties(e.target.value)}
              placeholder="Counties (comma sep)" className="h-9 px-2 border border-slate-200 rounded-lg text-sm" />
            <input value={newZips} onChange={e => setNewZips(e.target.value)}
              placeholder="Zip codes (comma sep)" className="h-9 px-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <button onClick={addTerritory} disabled={!newState}
            className="w-full py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 disabled:opacity-40 transition-colors">
            + Add State Territory
          </button>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 disabled:opacity-60"
            data-testid="save-territories-btn">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "lc_staff" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/lc/users", form);
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-slate-800">Add Team Member</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
            <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required
              placeholder="Jane Smith"
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30"
              data-testid="new-user-name" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Email Address</label>
            <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required
              placeholder="jane@company.com"
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30"
              data-testid="new-user-email" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Temporary Password</label>
            <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required minLength={8}
              placeholder="Min. 8 characters"
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30"
              data-testid="new-user-password" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Role</label>
            <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
              className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy/30"
              data-testid="new-user-role">
              <option value="lc_staff">Field Staff (Rep)</option>
              <option value="lc_manager">Manager</option>
            </select>
          </div>
          {error && <p className="text-red-500 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy/90 disabled:opacity-60"
              data-testid="create-user-submit-btn">
              {saving ? "Creating..." : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function LCDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = user?.role === "lc_manager" || user?.role === "admin";

  const [inspections, setInspections] = useState([]);
  const [team, setTeam] = useState([]);
  const [dispatchSettings, setDispatchSettings] = useState({ mode: "manual", threshold: 65 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("work-queue");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [assignModal, setAssignModal] = useState(null);
  const [territoryModal, setTerritoryModal] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [decisionModal, setDecisionModal] = useState(null); // needs_decision inspection

  const loadData = async () => {
    setLoading(true);
    try {
      const promises = [api.get("/inspections")];
      if (isManager) {
        promises.push(api.get("/lc/team"));
        promises.push(api.get("/lc/dispatch-settings"));
      }
      const results = await Promise.all(promises);
      setInspections(results[0].data);
      if (isManager && results[1]) setTeam(results[1].data);
      if (isManager && results[2]) setDispatchSettings(results[2].data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleAssign = async (inspId, userId) => {
    await api.put(`/inspections/${inspId}/assign`, { assigned_to: userId });
    await loadData();
  };

  const handleDispatchAction = async (inspId, action, assignedTo = null) => {
    const body = { action };
    if (assignedTo) body.assigned_to = assignedTo;
    await api.put(`/lc/dispatch/${inspId}/action`, body);
    await loadData();
  };

  const handleUpdateUser = async (userId, data) => {
    await api.put(`/lc/users/${userId}`, data);
    await loadData();
  };

  const filtered = inspections.filter(i => {
    if (filterStatus && i.status !== filterStatus) return false;
    if (filterAssignee === "unassigned" && i.assigned_to) return false;
    if (filterAssignee && filterAssignee !== "unassigned" && i.assigned_to !== filterAssignee) return false;
    if (search && !i.insured_name?.toLowerCase().includes(search.toLowerCase()) &&
        !i.property_address?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = isManager ? {
    total: inspections.length,
    unassigned: inspections.filter(i => !i.assigned_to).length,
    pending: inspections.filter(i => i.status === "pending" || i.status === "in_progress").length,
    teamSize: team.filter(u => u.active !== false).length,
  } : {
    assigned: inspections.length,
    pending: inspections.filter(i => i.status === "pending").length,
    inProgress: inspections.filter(i => i.status === "in_progress").length,
    needsReview: inspections.filter(i => i.status === "completed").length,
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-800">
              {isManager ? "Loss Control Module" : "My Work Queue"}
            </h1>
            <p className="text-slate-500 text-sm mt-0.5">
              {isManager ? "Manage your team and inspection assignments" : "Your assigned inspections"}
            </p>
          </div>
          {isManager && (
            <button
              data-testid="add-lc-user-btn"
              onClick={() => setShowCreateUser(true)}
              className="flex items-center gap-2 bg-navy text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-navy/90 transition-colors shadow-sm"
            >
              <UserPlus className="w-4 h-4" />
              Add Team Member
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(isManager ? [
            { label: "Total Inspections", value: stats.total, icon: ClipboardList, color: "text-navy", bg: "bg-blue-50" },
            { label: "Unassigned", value: stats.unassigned, icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
            { label: "Active", value: stats.pending, icon: Clock, color: "text-slate-600", bg: "bg-slate-100" },
            { label: "Team Members", value: stats.teamSize, icon: Users, color: "text-green-600", bg: "bg-green-50" },
          ] : [
            { label: "Assigned to Me", value: stats.assigned, icon: ClipboardList, color: "text-navy", bg: "bg-blue-50" },
            { label: "Pending", value: stats.pending, icon: Clock, color: "text-slate-600", bg: "bg-slate-100" },
            { label: "In Progress", value: stats.inProgress, icon: RefreshCw, color: "text-blue-600", bg: "bg-blue-50" },
            { label: "Needs Review", value: stats.needsReview, icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
          ]).map(stat => (
            <div key={stat.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-500 text-xs font-medium uppercase tracking-wider">{stat.label}</span>
                <div className={`w-8 h-8 ${stat.bg} rounded-lg flex items-center justify-center`}>
                  <stat.icon className={`w-4 h-4 ${stat.color}`} />
                </div>
              </div>
              <div className="font-heading text-3xl font-bold text-slate-800"
                data-testid={`lc-stat-${stat.label.toLowerCase().replace(/\s+/g,"-")}`}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Manager Tabs */}
        {isManager && (
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit flex-wrap">
            {[
              { key: "work-queue", label: "Work Queue", icon: ClipboardList },
              { key: "territory-map", label: "Territory Map", icon: MapPin },
              { key: "dispatch", label: "Dispatch Settings", icon: Settings },
              { key: "my-team", label: "My Team", icon: Users },
            ].map(t => (
              <button
                key={t.key}
                data-testid={`lc-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  tab === t.key ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Work Queue (manager) or My Work (staff) */}
        {(!isManager || tab === "work-queue") && (
          <>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search insured or address..."
                    className="w-full pl-9 pr-3 h-9 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30"
                    data-testid="lc-search" />
                </div>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  className="h-9 px-3 border border-slate-200 rounded-lg text-sm text-slate-600 bg-white"
                  data-testid="lc-filter-status">
                  <option value="">All Statuses</option>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                {isManager && (
                  <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}
                    className="h-9 px-3 border border-slate-200 rounded-lg text-sm text-slate-600 bg-white"
                    data-testid="lc-filter-assignee">
                    <option value="">All Assignees</option>
                    <option value="unassigned">Unassigned</option>
                    {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                )}
                <button onClick={loadData}
                  className="h-9 w-9 flex items-center justify-center border border-slate-200 rounded-lg text-slate-500 hover:text-navy hover:border-navy transition-colors"
                  data-testid="lc-refresh-btn">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-medium text-slate-700 text-sm">
                  {filtered.length} inspection{filtered.length !== 1 ? "s" : ""}
                </h2>
              </div>
              {loading ? (
                <div className="py-16 text-center text-slate-400">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                  <p className="text-sm">Loading inspections...</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-16 text-center">
                  <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No inspections found</p>
                  {!isManager && <p className="text-slate-400 text-sm mt-1">No inspections are assigned to you yet</p>}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="lc-inspections-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Insured</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Type</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Address</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Risk</th>
                        {isManager && (
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Dispatch</th>
                        )}
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filtered.map(insp => (
                        <tr key={insp.id} className="hover:bg-slate-50 transition-colors" data-testid={`lc-row-${insp.id}`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">{insp.insured_name}</div>
                            <div className="text-xs text-slate-400 mt-0.5">{insp.policy_number || "No policy #"}</div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-slate-600">{TYPE_LABELS[insp.inspection_type] || insp.inspection_type}</td>
                          <td className="px-4 py-3 hidden sm:table-cell text-slate-500 text-xs">{insp.property_address}</td>
                          <td className="px-4 py-3"><StatusBadge status={insp.status} /></td>
                          <td className="px-4 py-3 hidden xl:table-cell">
                            <RiskBadge score={insp.risk_score} level={insp.risk_level} threshold={dispatchSettings.threshold} mode={dispatchSettings.mode} />
                          </td>
                          {isManager && (
                            <td className="px-4 py-3 hidden lg:table-cell">
                              {insp.dispatch_status ? (
                                <DispatchStatusBadge dispatchStatus={insp.dispatch_status} />
                              ) : insp.assigned_to_name ? (
                                <span className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                                  <UserCheck className="w-3 h-3 text-green-500" />
                                  {insp.assigned_to_name}
                                </span>
                              ) : (
                                <span className="text-xs text-amber-500 font-medium italic">Unassigned</span>
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 justify-end flex-wrap">
                              {/* Pre-Briefing button — always shown */}
                              <button
                                data-testid={`open-briefing-btn-${insp.id}`}
                                onClick={() => navigate(`/lc/briefing/${insp.id}`)}
                                className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 hover:bg-navy hover:text-white rounded-md text-xs font-medium transition-colors"
                              >
                                <BookOpen className="w-3 h-3" /> Briefing
                              </button>
                              {/* Field Visit button — assigned inspections not yet visit-completed */}
                              {insp.assigned_to && insp.dispatch_status !== "visit_completed" && insp.dispatch_status !== "completed_no_visit" && (
                                <button
                                  data-testid={`field-visit-btn-${insp.id}`}
                                  onClick={() => navigate(`/lc/visit/${insp.id}`)}
                                  className="flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white rounded-md text-xs font-medium transition-colors"
                                >
                                  <ClipboardCheck className="w-3 h-3" /> Field Visit
                                </button>
                              )}
                              {/* Dispatch Decision buttons for needs_decision (manager only) */}
                              {isManager && insp.dispatch_status === "needs_decision" && (
                                <button
                                  data-testid={`review-decision-btn-${insp.id}`}
                                  onClick={() => setDecisionModal(insp)}
                                  className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 text-white hover:bg-amber-600 rounded-md text-xs font-medium transition-colors animate-pulse"
                                >
                                  <AlertTriangle className="w-3 h-3" /> Review
                                </button>
                              )}
                              {/* Regular Assign/Reassign button (for non-dispatch or auto_dispatched) */}
                              {isManager && insp.dispatch_status !== "needs_decision" && (
                                <button
                                  data-testid={`assign-btn-${insp.id}`}
                                  onClick={() => setAssignModal(insp)}
                                  className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 hover:bg-navy hover:text-white rounded-md text-xs font-medium transition-colors"
                                >
                                  <UserCheck className="w-3 h-3" />
                                  {insp.assigned_to ? "Reassign" : "Assign"}
                                </button>
                              )}
                              {(insp.status === "completed" || insp.status === "reviewed" || insp.status === "finalized") && (
                                <button
                                  data-testid={`lc-review-btn-${insp.id}`}
                                  onClick={() => navigate(`/dashboard/review/${insp.id}`)}
                                  className="flex items-center gap-1 px-2.5 py-1 bg-navy/10 text-navy hover:bg-navy hover:text-white rounded-md text-xs font-medium transition-colors"
                                >
                                  <Eye className="w-3 h-3" /> View
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Territory Map Tab (manager only) */}
        {isManager && tab === "territory-map" && (
          <TerritoryMapTab team={team} />
        )}

        {/* Dispatch Settings Tab (manager only) */}
        {isManager && tab === "dispatch" && (
          <DispatchSettingsTab isAdmin={user?.role === "admin"} />
        )}

        {/* My Team Tab (manager only) */}
        {isManager && tab === "my-team" && (
          <div className="space-y-4">
            {loading ? (
              <div className="py-16 text-center text-slate-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" /></div>
            ) : team.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No team members yet</p>
                <p className="text-slate-400 text-sm mt-1">Add your first loss control team member to get started</p>
                <button onClick={() => setShowCreateUser(true)}
                  className="mt-4 bg-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-navy/90">
                  Add Team Member
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="team-grid">
                {team.map(member => {
                  const memberWork = inspections.filter(i => i.assigned_to === member.id);
                  const activeWork = memberWork.filter(i => !["finalized"].includes(i.status));
                  return (
                    <div key={member.id}
                      className={`bg-white border rounded-xl p-5 transition-all ${member.active === false ? "opacity-50 border-slate-100" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"}`}
                      data-testid={`team-card-${member.id}`}>
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-11 h-11 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-navy font-bold text-base">{member.name?.[0]?.toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{member.name}</p>
                          <p className="text-xs text-slate-400 truncate">{member.email}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium mt-1 inline-block ${
                            member.role === "lc_manager" ? "bg-navy/10 text-navy" : "bg-emerald-50 text-emerald-700"
                          }`}>
                            {member.role === "lc_manager" ? "Manager" : "Field Staff"}
                          </span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 font-semibold">Territories</p>
                        {member.territories?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {member.territories.map(t => (
                              <span key={t.state} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md text-xs font-semibold">
                                <MapPin className="w-2.5 h-2.5" /> {t.state}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 italic">No territories assigned</p>
                        )}
                      </div>

                      <div className="flex items-center justify-between text-xs p-2.5 bg-slate-50 rounded-lg mb-4">
                        <span className="text-slate-500">{activeWork.length} active assignment{activeWork.length !== 1 ? "s" : ""}</span>
                        {member.role === "lc_manager" && (
                          <span className={`font-semibold ${member.auto_assign_enabled !== false ? "text-green-600" : "text-slate-400"}`}>
                            Auto-assign: {member.auto_assign_enabled !== false ? "ON" : "OFF"}
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          data-testid={`edit-territories-btn-${member.id}`}
                          onClick={() => setTerritoryModal(member)}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-slate-200 rounded-lg text-xs text-slate-600 hover:border-navy hover:text-navy transition-colors font-medium"
                        >
                          <MapPin className="w-3.5 h-3.5" /> Edit Territories
                        </button>
                        <button
                          data-testid={`view-work-btn-${member.id}`}
                          onClick={() => { setTab("work-queue"); setFilterAssignee(member.id); }}
                          className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-slate-50 rounded-lg text-xs text-slate-600 hover:bg-navy/10 hover:text-navy transition-colors font-medium"
                        >
                          <Eye className="w-3.5 h-3.5" /> View Work
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {assignModal && (
        <AssignModal inspection={assignModal} team={team} onAssign={handleAssign} onClose={() => setAssignModal(null)} />
      )}
      {territoryModal && (
        <TerritoryEditor staffUser={territoryModal} onSave={handleUpdateUser} onClose={() => setTerritoryModal(null)} />
      )}
      {showCreateUser && (
        <CreateUserModal onClose={() => setShowCreateUser(false)} onCreated={loadData} />
      )}
      {decisionModal && (
        <DispatchDecisionModal
          inspection={decisionModal}
          team={team}
          onAction={handleDispatchAction}
          onClose={() => setDecisionModal(null)}
        />
      )}
    </Layout>
  );
}
